/**
 * Navigation patterns — top bar (primary), sub-nav (in-section), breadcrumbs.
 * The Universal* layout components consume these.
 *
 * Architecture decision (2026-04-29): the portal uses TOP-BAR-ONLY navigation —
 * no sidebar. Primary nav lives in the top bar; in-section navigation uses
 * UniversalSubNav (a second horizontal row) or route-aware tabs on detail pages.
 *
 * Token note (theme-bridge): surface colors flow through `bg-card` /
 * `text-foreground` / `border-border` / `bg-muted` so the same primitives
 * render correctly in the portal (light) and marketing (dark) themes.
 */

export const navigationPatterns = {
  topBar: {
    /** Flex row: brand (shrink-0, left) · nav (flex-1, centred contents) ·
     *  right cluster (shrink-0, right). Compact h-16 (was h-24) to match the
     *  marketing site's top-bar rhythm — see PORTAL_VISUAL_ALIGNMENT_IMPLEMENTATION_PLAN_2026-05-08.md §VA2.1.
     *  The bar uses a hero-image background with a dark overlay (rendered as
     *  layered children inside the component); text sits above via `relative z-10`. */
    wrapper:
      'sticky top-0 z-30 relative flex h-16 w-full items-center gap-6 px-6 text-white shadow-md overflow-hidden',
    brand: 'relative z-10 flex items-center gap-3 shrink-0',
    /** flex-1 fills the middle so the nav items center themselves between
     *  the brand and the right cluster. min-w-0 lets the slot shrink on
     *  narrow viewports; overflow-x-auto preserves all items via horizontal
     *  scroll instead of dropping them. */
    /** DESKTOP ONLY (Phase 4). Below `md` this is hidden and
     *  `UniversalMobileNav` takes over; between `md` and `lg` the links render
     *  icon-only, because 8–9 labelled items do not fit a 768–1024px bar.
     *
     *  overflow-x-auto + the scrollbar utilities are now a SAFETY NET, not the
     *  strategy: Windows/Chrome paints a ~15px track inside the h-16 bar the
     *  moment content overflows, which is what produced the original
     *  "scrollbar in the nav" report. With the breakpoints above it should no
     *  longer engage; if it does, it degrades silently instead of eating
     *  vertical space. */
    primaryCluster:
      'relative z-10 hidden flex-1 items-center justify-center gap-1 min-w-0 md:flex overflow-x-auto ' +
      '[scrollbar-width:none] [&::-webkit-scrollbar]:hidden',
    rightCluster: 'relative z-10 flex items-center gap-2 shrink-0',
    /** Marketing-aligned voice: small (13px) tracking-wide, white/70 → white,
     *  with an animated 2px primary bar that grows from centre on hover and
     *  is full-width when active. Replaces the old pill+ring indicator. */
    link: 'group relative inline-flex items-center gap-2 px-3 py-2 text-[13px] font-medium tracking-wide text-white/70 transition-colors duration-200 hover:text-white',
    linkActive:
      'group relative inline-flex items-center gap-2 px-3 py-2 text-[13px] font-medium tracking-wide text-white transition-colors duration-200',
    /** Indicator span — rendered as a sibling inside each TopNavLink. */
    linkIndicator:
      'pointer-events-none absolute inset-x-3 -bottom-px h-[2px] origin-center bg-primary transition-transform duration-300 ease-out',
    linkIndicatorActive: 'scale-x-100',
    linkIndicatorInactive: 'scale-x-0 group-hover:scale-x-50',
    menuTrigger:
      'inline-flex items-center gap-1.5 rounded-sm px-3 py-2 text-sm font-medium text-white/80 hover:bg-white/10 hover:text-white',
  },
  subNav: {
    /** sticky offset matches the new topBar h-16. */
    wrapper:
      'sticky top-16 z-20 flex h-12 items-center gap-1 overflow-x-auto border-b border-border bg-card px-6',
    link: 'inline-flex items-center gap-2 whitespace-nowrap rounded-sm px-3 py-1.5 text-sm text-muted-foreground hover:bg-muted hover:text-foreground',
    linkActive:
      'inline-flex items-center gap-2 whitespace-nowrap rounded-sm bg-primary/10 px-3 py-1.5 text-sm font-medium text-primary',
  },
  breadcrumb: {
    wrapper: 'flex items-center gap-1.5 text-sm text-muted-foreground',
    separator: 'text-muted-foreground',
    current: 'font-medium text-foreground',
  },
} as const;
